import { useSuspenseQuery } from '@tanstack/react-query';
import {
    getAuth,
    type IdTokenResult,
    onAuthStateChanged,
    onIdTokenChanged,
    type User,
} from 'firebase/auth';
import * as React from 'react';

import type { UserCustomClaims } from '@gitbook/app-database';
import { assert } from '@gitbook/util-ts';

import type { FirebaseAuthProviderId } from './types';

/**
 * Firebase listeners are triggered at initialization.
 * Wrap a callback to skip the first call.
 */
function skipFirstCall(callback: () => void): () => void {
    let firstCall = true;

    return () => {
        if (firstCall) {
            firstCall = false;
        } else {
            callback();
        }
    };
}

/**
 * Subscription to access the firebase user.
 */
const currentUserSubscription = {
    getCurrentValue: () => getAuth().currentUser,
    subscribe: (callback) => {
        const auth = getAuth();
        const unsubscribeTokenChanged = onIdTokenChanged(auth, skipFirstCall(callback));
        const unsubscribeStateChanged = onAuthStateChanged(auth, skipFirstCall(callback));

        return () => {
            unsubscribeTokenChanged();
            unsubscribeStateChanged();
        };
    },
};

/**
 * Get the current firebase user.
 */
export function useCurrentUser(): User | null {
    return React.useSyncExternalStore(
        currentUserSubscription.subscribe,
        currentUserSubscription.getCurrentValue
    );
}

/**
 * Get the current firebase user or throw if not authenticated.
 */
export function useLoggedInUser(): User {
    const currentUser = useCurrentUser();
    assert(currentUser, 'This component requires an authenticated user.');
    return currentUser;
}

type TokenResult = IdTokenResult & {
    claims: UserCustomClaims;
};

/**
 * Return the id token result of to the current user.
 */
export function useUserIdTokenResult(): TokenResult | null {
    const auth = getAuth();
    const { data, refetch } = useSuspenseQuery({
        queryKey: ['userIdTokenResult'],
        staleTime: 0,
        queryFn: async () => {
            return auth.currentUser?.getIdTokenResult() ?? null;
        },
    });
    React.useEffect(
        () =>
            auth.onIdTokenChanged(
                skipFirstCall(() => {
                    refetch();
                })
            ),
        [auth.onIdTokenChanged, refetch]
    );
    return data;
}

/**
 * Return the custom claims linked to the current user.
 */
export function useUserCustomClaims(): UserCustomClaims {
    const userIdTokenResult = useUserIdTokenResult();
    if (userIdTokenResult === null) {
        const claims: UserCustomClaims = {};
        return claims;
    }
    return userIdTokenResult.claims;
}

/**
 * Listen to the current user authentication providers.
 */
export function useUserProviders(): FirebaseAuthProviderId[] {
    const currentUser = useLoggedInUser();

    return React.useMemo(
        () => currentUser.providerData.map((info) => info.providerId as FirebaseAuthProviderId),
        [currentUser]
    );
}
